import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CTAButton } from './CTAButton';
import { useCurrency } from '../hooks/useCurrency';
import { useOrderBumps } from '../hooks/useOrderBumps';
import { Download, Smartphone, ShieldCheck, Gift, Check, Sparkles, ChevronDown } from 'lucide-react';
export function PricingSection() {
  const {
    formatPrice
  } = useCurrency();
  const {
    bump1,
    bump2,
    setBump1,
    setBump2,
    totalPrice,
    goToCheckout
  } = useOrderBumps();
  const [showBonusDetails, setShowBonusDetails] = useState(false);
  return <section id="pricing" className="relative py-12 md:py-20 px-6 md:px-8 overflow-hidden" style={{
    background: 'linear-gradient(160deg, #2C2420 0%, #3D3229 45%, #4a3d34 100%)'
  }} data-id="element-460">
      {/* Ambient texture blobs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-terracotta/10 rounded-full blur-[100px] pointer-events-none" data-id="element-461" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-sage/10 rounded-full blur-[100px] pointer-events-none" data-id="element-462" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-terracotta/5 rounded-full blur-[120px] pointer-events-none" data-id="element-463" />

      <motion.div initial={{
      opacity: 0,
      y: 24
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-100px'
    }} transition={{
      duration: 0.7,
      ease: 'easeOut'
    }} className="relative max-w-xl mx-auto text-center" data-id="element-464">
        {/* Pre-headline copy */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="mb-12" data-id="element-465">
          <p className="text-xs uppercase tracking-[0.25em] text-terracotta/80 font-bold mb-6" data-id="element-466">
            Your reset starts here
          </p>
          <p className="font-serif text-3xl md:text-4xl text-cream/90 leading-snug mb-3" data-id="element-467">
            You don't need better naps.
          </p>
          <p className="font-serif text-4xl md:text-5xl text-cream font-medium leading-tight mb-8" data-id="element-468">
            You need <span className="italic text-terracotta" data-id="element-469">30 minutes.</span>
          </p>

          {/* Thin accent line */}
          <div className="w-12 h-px bg-terracotta/40 mx-auto mb-8" data-id="element-470" />

          <div className="space-y-4 mb-10" data-id="element-471">
            {[{
            text: 'A 7-day structure you can start today',
            highlight: true
          }, {
            text: 'A daily reset that protects your body and mind',
            highlight: true
          }, {
            text: '3 bonus tools to ask for help without guilt',
            highlight: true
          }].map((line, i) => <div key={i} className="flex items-center justify-center gap-3" data-id="element-472">
                <div className="w-1.5 h-1.5 rounded-full bg-terracotta/60 shrink-0" data-id="element-473" />
                <p className={`text-base md:text-lg ${line.highlight ? 'text-cream/80' : 'text-cream/50'}`} data-id="element-474">
                  {line.text}
                </p>
              </div>)}
          </div>

          <p className="text-cream/40 text-sm italic mb-10" data-id="element-475">
            Not cry-it-out. Not another sleep course. Just calm, clear
            structure.
          </p>
        </motion.div>

        {/* Premium Pricing Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="relative rounded-[2rem] overflow-hidden mb-8" style={{
        background: 'linear-gradient(180deg, rgba(250,247,242,0.08) 0%, rgba(250,247,242,0.03) 100%)',
        border: '1px solid rgba(250,247,242,0.15)',
        boxShadow: '0 24px 48px rgba(0,0,0,0.2), inset 0 1px 0 rgba(250,247,242,0.2)',
        backdropFilter: 'blur(20px)'
      }} data-id="element-476">
          {/* Animated top shimmer */}
          <motion.div animate={{
          x: ['-100%', '200%']
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear',
          repeatDelay: 2
        }} className="absolute top-0 left-0 w-1/2 h-px bg-gradient-to-r from-transparent via-cream/50 to-transparent" data-id="element-477" />

          <div className="p-8 md:p-10" data-id="element-478">
            {/* Product Header */}
            <div className="mb-8" data-id="element-479">
              <div className="inline-flex items-center gap-1.5 bg-terracotta/20 border border-terracotta/30 text-terracotta text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4" data-id="element-480">
                <Sparkles className="w-3 h-3" data-id="element-481" />
                Instant Access
              </div>
              <h3 className="font-serif text-2xl md:text-3xl text-cream mb-2" data-id="element-482">
                The Nap-Trapped Reset
              </h3>
              <p className="text-cream/60 text-sm" data-id="element-483">
                A simple 7-day plan. A clear daily reset.
              </p>
            </div>

            {/* Value Breakdown */}
            <div className="mb-10 space-y-4 text-left bg-black/15 rounded-2xl p-6 border border-cream/5" data-id="element-484">
              <div className="flex justify-between items-center text-sm" data-id="element-485">
                <span className="text-cream/80 font-medium tracking-wide" data-id="element-486">
                  The Nap-Trapped Reset Guide
                </span>
                <span className="text-cream/40 font-serif italic line-through" data-id="element-487">
                  {formatPrice(45)} value
                </span>
              </div>
              <div className="flex justify-between items-center text-sm" data-id="element-488">
                <span className="text-cream/80 font-medium tracking-wide" data-id="element-489">
                  3 Premium Bonus Tools
                </span>
                <span className="text-cream/40 font-serif italic line-through" data-id="element-490">
                  {formatPrice(51)} value
                </span>
              </div>
              <div className="pt-4 border-t border-cream/10 flex justify-between items-center" data-id="element-491">
                <span className="text-cream/50 text-[10px] uppercase tracking-[0.2em] font-bold" data-id="element-492">
                  Total Value
                </span>
                <span className="text-cream/40 font-serif text-lg line-through" data-id="element-493">
                  {formatPrice(96)}
                </span>
              </div>
            </div>

            {/* Price Display */}
            <div className="mb-10 relative" data-id="element-494">
              <div className="absolute -top-5 right-0 md:right-4 bg-terracotta text-white text-[10px] font-bold uppercase tracking-widest px-3.5 py-1.5 rounded-full shadow-lg transform rotate-2" data-id="element-495">
                Save 65%
              </div>
              <div className="flex items-baseline justify-center gap-2 md:gap-3 mb-2" data-id="element-496">
                <span className="font-serif text-cream/50 text-lg md:text-2xl italic" data-id="element-497">
                  Only
                </span>
                <span className="font-serif text-5xl md:text-7xl font-bold text-cream tracking-tight leading-none drop-shadow-sm" data-id="element-498">
                  {formatPrice(17)}
                </span>
              </div>
              <p className="text-cream/40 text-xs uppercase tracking-widest font-medium" data-id="element-499">
                One-time payment · Yours forever
              </p>
            </div>

            {/* Bonus stack summary */}
            <div className="mb-8 rounded-2xl p-5 md:p-6 text-left relative overflow-hidden" style={{
            background: 'linear-gradient(135deg, rgba(196,133,106,0.1) 0%, rgba(250,247,242,0.02) 100%)',
            border: '1px solid rgba(196,133,106,0.2)'
          }} data-id="element-500">
              <div className="absolute top-0 right-0 w-32 h-32 bg-terracotta/10 rounded-full blur-2xl -mr-10 -mt-10" data-id="element-501" />

              <div className="flex items-center gap-2 mb-5 relative z-10" data-id="element-502">
                <Gift className="w-4 h-4 text-terracotta" data-id="element-503" />
                <p className="text-xs uppercase tracking-[0.2em] text-terracotta font-bold" data-id="element-504">
                  Free Bonus Stack Included
                </p>
              </div>

              <div className="space-y-4 relative z-10" data-id="element-505">
                {[{
                name: 'The Body Back in 30 Reset Menu',
                image: 'https://cdn.magicpatterns.com/uploads/gj3HysKSw4wcCqLVvZKhfJ/bono1.png',
                description: 'When you finally get a window… the last thing you need is decision fatigue. This is a clean, pick-one menu of 12 simple options to use your 30 minutes on purpose—so you step back into the day lighter.',
                includes: ["12 'pick-1' reset options (shower, food, downshift, sunlight, stretch, etc.)", 'The simple 5–20–5 structure (land → reset → close)', "'Only got 10 minutes?' mini version", '1-minute Before/After check-in']
              }, {
                name: "The 'Someone Else's Arms' Handoff Guide",
                image: 'https://cdn.magicpatterns.com/uploads/htb81b6jSf8DQ4YtE2tBq8/bono2.png',
                description: "Support shouldn't require a 10-minute explanation… every time. This is the calm, one-sheet handoff system you can give to a grandparent, sitter, or friend—so you can step away for 30–60 minutes without spiraling.",
                includes: ['3 simple handoff sheets (different situations)', "'Before you leave the room' checklist", 'Ready-to-send texts to set expectations gently', 'Printable + phone-friendly version']
              }, {
                name: "The 'I Need Help' Text Script Kit",
                image: 'https://cdn.magicpatterns.com/uploads/gs7bBNjs5HcyXxCHdEFney/bono3.png',
                description: "Sometimes the hardest part isn't the nap. It's asking for help—without guilt, without over-explaining, without feeling like you have to justify it. This kit gives you 18 copy-paste messages you can send in 30 seconds.",
                includes: ['18 scripts (partner, grandparents, friend, sitter)', 'Simple message formula: time + task + duration + kind close', "Follow-ups that don't feel awkward", "'My non-negotiables' template (so your help actually helps)"]
              }].map((bonus, i) => <div key={i} className="flex items-center gap-3 bg-black/10 rounded-xl p-2 pr-4 border border-cream/5" data-id="element-506">
                    <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 border border-cream/10 shadow-sm relative" data-id="element-507">
                      <img src={bonus.image} alt={bonus.name} loading="lazy" className="w-full h-full object-cover" data-id="element-508" />
                    </div>
                    <span className="text-cream/80 text-sm leading-snug flex-1" data-id="element-509">
                      {bonus.name}
                    </span>
                    <span className="text-terracotta text-[10px] font-bold uppercase tracking-wider bg-terracotta/10 px-2 py-1 rounded" data-id="element-510">
                      Free
                    </span>
                  </div>)}
              </div>

              {/* Expandable bonus details */}
              <AnimatePresence data-id="element-511">
                {showBonusDetails && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.4,
                ease: [0.25, 0.1, 0.25, 1]
              }} className="overflow-hidden relative z-10" data-id="element-512">
                    <div className="pt-5 space-y-5" data-id="element-513">
                      {[{
                    name: 'The Body Back in 30 Reset Menu',
                    image: 'https://cdn.magicpatterns.com/uploads/gj3HysKSw4wcCqLVvZKhfJ/bono1.png',
                    description: 'When you finally get a window… the last thing you need is decision fatigue. This is a clean, pick-one menu of 12 simple options to use your 30 minutes on purpose—so you step back into the day lighter.',
                    includes: ["12 'pick-1' reset options (shower, food, downshift, sunlight, stretch, etc.)", 'The simple 5–20–5 structure (land → reset → close)', "'Only got 10 minutes?' mini version", '1-minute Before/After check-in']
                  }, {
                    name: "The 'Someone Else's Arms' Handoff Guide",
                    image: 'https://cdn.magicpatterns.com/uploads/htb81b6jSf8DQ4YtE2tBq8/bono2.png',
                    description: "Support shouldn't require a 10-minute explanation… every time. This is the calm, one-sheet handoff system you can give to a grandparent, sitter, or friend—so you can step away for 30–60 minutes without spiraling.",
                    includes: ['3 simple handoff sheets (different situations)', "'Before you leave the room' checklist", 'Ready-to-send texts to set expectations gently', 'Printable + phone-friendly version']
                  }, {
                    name: "The 'I Need Help' Text Script Kit",
                    image: 'https://cdn.magicpatterns.com/uploads/gs7bBNjs5HcyXxCHdEFney/bono3.png',
                    description: "Sometimes the hardest part isn't the nap. It's asking for help—without guilt, without over-explaining, without feeling like you have to justify it. This kit gives you 18 copy-paste messages you can send in 30 seconds.",
                    includes: ['18 scripts (partner, grandparents, friend, sitter)', 'Simple message formula: time + task + duration + kind close', "Follow-ups that don't feel awkward", "'My non-negotiables' template (so your help actually helps)"]
                  }].map((bonus, i) => <div key={i} className="rounded-2xl overflow-hidden border border-cream/8" style={{
                    background: 'linear-gradient(160deg, rgba(250,247,242,0.06) 0%, rgba(250,247,242,0.02) 100%)'
                  }} data-id="element-514">
                          {/* Mockup image */}
                          <div className="relative w-full aspect-[16/9] overflow-hidden" data-id="element-515">
                            <img src={bonus.image} alt={bonus.name} loading="lazy" className="w-full h-full object-cover" data-id="element-516" />
                            <span className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-wider bg-terracotta text-white px-2.5 py-1 rounded-full shadow-md" data-id="element-517">
                              Free
                            </span>
                          </div>

                          {/* Content */}
                          <div className="p-5" data-id="element-518">
                            <p className="text-terracotta text-xs font-bold uppercase tracking-wider mb-3" data-id="element-519">
                              {bonus.name}
                            </p>
                            <p className="text-cream/60 text-sm leading-relaxed mb-4" data-id="element-520">
                              {bonus.description}
                            </p>
                            <div className="space-y-2.5" data-id="element-521">
                              {bonus.includes.map((item, j) => <div key={j} className="flex items-start gap-2.5" data-id="element-522">
                                  <Check className="w-3.5 h-3.5 text-sage shrink-0 mt-0.5" data-id="element-523" />
                                  <span className="text-cream/70 text-xs leading-relaxed" data-id="element-524">
                                    {item}
                                  </span>
                                </div>)}
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>

              {/* Read more toggle */}
              <button onClick={() => setShowBonusDetails(!showBonusDetails)} className="flex items-center gap-1.5 mx-auto mt-5 text-terracotta/80 hover:text-terracotta text-xs font-semibold uppercase tracking-wider transition-colors duration-200 relative z-10" data-id="element-525">
                {showBonusDetails ? 'Read less' : 'Read more'}
                <motion.span animate={{
                rotate: showBonusDetails ? 180 : 0
              }} transition={{
                duration: 0.3
              }} data-id="element-526">
                  <ChevronDown className="w-3.5 h-3.5" data-id="element-527" />
                </motion.span>
              </button>
            </div>

            {/* Emotional close */}
            <div id="pricing-cta" className="mb-8 space-y-1 text-cream/70 text-sm" data-id="element-528">
              <p data-id="element-529">You don't have to wait for this phase to pass.</p>
              <p className="text-cream font-medium" data-id="element-530">
                You can feel more stable today.
              </p>
            </div>

            {/* Payment info */}
            {/* Payment info with icons */}
            <div className="text-center mb-6" data-id="element-531">
              <div className="flex items-center justify-center gap-2 mb-3" data-id="element-532">
                <svg className="w-3 h-3 text-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-533">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" data-id="element-534" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" data-id="element-535" />
                </svg>
                <p className="text-cream/50 text-xs tracking-wide" data-id="element-536">
                  Secure checkout — pay in one tap
                </p>
              </div>
              <div className="flex items-center justify-center gap-2" data-id="element-537">
                {/* Shop Pay */}
                <div className="bg-cream/12 border border-cream/15 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5" data-id="element-538">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" data-id="element-539">
                    <rect width="24" height="24" rx="4" fill="#5A31F4" data-id="element-540" />
                    <path d="M7.5 8.5c0-.8.7-1.5 1.5-1.5h1c1.7 0 3 1.3 3 3s-1.3 3-3 3H9v2.5c0 .3-.2.5-.5.5h-.5c-.3 0-.5-.2-.5-.5V8.5z" fill="white" data-id="element-541" />
                    <path d="M14.5 10c0-.3.2-.5.5-.5h.5c.3 0 .5.2.5.5v5.5c0 .3-.2.5-.5.5H15c-.3 0-.5-.2-.5-.5V10z" fill="white" data-id="element-542" />
                  </svg>
                  <span className="text-cream/70 text-[9px] font-semibold tracking-wide" data-id="element-543">
                    Shop Pay
                  </span>
                </div>
                {/* PayPal */}
                <div className="bg-cream/12 border border-cream/15 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5" data-id="element-544">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" data-id="element-545">
                    <rect width="24" height="24" rx="4" fill="#003087" data-id="element-546" />
                    <path d="M8.5 7h3.5c1.5 0 2.5.8 2.5 2.2 0 2-1.5 3.3-3.5 3.3H9.8l-.5 3H7.5L8.5 7z" fill="#fff" data-id="element-547" />
                    <path d="M13 8.5h2.5c1.2 0 2 .7 2 1.8 0 1.8-1.3 3-3 3h-1l-.4 2.5h-1.5L13 8.5z" fill="#009cde" data-id="element-548" />
                  </svg>
                  <span className="text-cream/70 text-[9px] font-semibold tracking-wide" data-id="element-549">
                    PayPal
                  </span>
                </div>
                {/* Google Pay */}
                <div className="bg-cream/12 border border-cream/15 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5" data-id="element-550">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" data-id="element-551">
                    <rect width="24" height="24" rx="4" fill="#fff" fillOpacity="0.15" data-id="element-552" />
                    <path d="M12.24 10.285V13.4h4.3c-.2 1.1-1.3 3.2-4.3 3.2-2.6 0-4.7-2.1-4.7-4.7s2.1-4.7 4.7-4.7c1.5 0 2.5.6 3 1.2l2-2C16 5.2 14.3 4.5 12.24 4.5 8 4.5 4.5 8 4.5 12.2s3.5 7.7 7.74 7.7c4.5 0 7.4-3.1 7.4-7.5 0-.5-.1-.9-.1-1.3h-7.3z" fill="#4285F4" data-id="element-553" />
                  </svg>
                  <span className="text-cream/70 text-[9px] font-semibold tracking-wide" data-id="element-554">
                    Google Pay
                  </span>
                </div>
              </div>
            </div>

            {/* Price reminder above CTA */}
            <div className="flex items-baseline justify-center gap-3 mb-5" data-id="element-555">
              <span className="font-serif text-cream/30 text-lg line-through" data-id="element-556">
                {formatPrice(45 + (bump1 ? 14 : 0) + (bump2 ? 8 : 0))}
              </span>
              <span className="font-serif text-4xl md:text-5xl font-bold text-cream tracking-tight" data-id="element-557">
                {formatPrice(totalPrice)}
              </span>
              <span className="bg-terracotta text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full shadow-sm ml-1 transform -translate-y-2" data-id="element-558">
                Save 65%
              </span>
            </div>

            {/* CTA */}
            <div className="relative mb-6" data-id="element-559">
              {/* Subtle pulsing glow behind button */}
              <div className="absolute -inset-1 bg-terracotta/30 rounded-full blur-md animate-pulse" data-id="element-560" />
              <CTAButton text="Start My Gentle Reset" onClick={goToCheckout} fullWidth className="relative text-xl py-5" data-id="element-561" />
            </div>

            {/* Under-button trust */}
            <div className="flex flex-col items-center gap-2 mb-8" data-id="element-562">
              <span className="flex items-center gap-1.5 text-[10px] text-cream/35" data-id="element-563">
                <Download className="w-2.5 h-2.5" data-id="element-564" /> Instant download
                <span className="text-cream/20 mx-0.5" data-id="element-565">·</span>
                <Smartphone className="w-2.5 h-2.5" data-id="element-566" /> Phone-friendly
              </span>
              <span className="flex items-center gap-2 text-xs text-sage font-medium" data-id="element-567">
                <ShieldCheck className="w-3.5 h-3.5" data-id="element-568" /> 14-Day Gentle Guarantee
              </span>
            </div>

            {/* Order Bumps */}
            <div className="space-y-4" data-id="element-569">
              {/* Section header */}
              <div className="text-center mb-2" data-id="element-570">
                <div className="flex items-center gap-3 justify-center mb-2" data-id="element-571">
                  <div className="h-px w-8 bg-cream/15" data-id="element-572" />
                  <p className="text-[10px] uppercase tracking-[0.2em] text-terracotta/70 font-bold" data-id="element-573">
                    Enhance Your Reset
                  </p>
                  <div className="h-px w-8 bg-cream/15" data-id="element-574" />
                </div>
                <p className="text-cream/40 text-xs italic font-serif" data-id="element-575">
                  Most moms add at least one
                </p>
              </div>

              {/* Bump 1 — Emergency Calm Cards */}
              <div onClick={() => setBump1(!bump1)} className="relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 order-bump-shimmer" style={{
              background: bump1 ? 'linear-gradient(160deg, rgba(139,158,129,0.06) 0%, #FAF7F2 100%)' : '#FAF7F2',
              border: bump1 ? '1.5px solid #8B9E81' : '1px solid #E5DDD3',
              boxShadow: bump1 ? '0 4px 24px rgba(139,158,129,0.15), 0 0 0 1px rgba(139,158,129,0.1)' : '0 2px 12px rgba(61,50,41,0.04)'
            }} data-id="element-576">
                {/* Popular badge */}
                <div className="absolute top-3 right-3 z-10" data-id="element-577">
                  <span className="bg-terracotta text-white text-[9px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md" data-id="element-578">
                    Popular
                  </span>
                </div>

                {/* Eyebrow label */}
                <div className="px-5 pt-4 pb-0" data-id="element-579">
                  <div className="flex items-center gap-2 mb-3" data-id="element-580">
                    <svg className="w-3.5 h-3.5 text-[#8B9E81]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-581">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" data-id="element-582" />
                    </svg>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-[#8B9E81] font-bold" data-id="element-583">
                      Add-on
                    </span>
                  </div>
                </div>

                {/* Mockup image */}
                <div className="px-5 mb-4" data-id="element-584">
                  <div className="rounded-xl overflow-hidden" style={{
                  boxShadow: '0 6px 20px rgba(61,50,41,0.08)'
                }} data-id="element-585">
                    <img src="https://cdn.magicpatterns.com/uploads/dsM2NX64GWS1g4GnazJpeQ/mockup_1orderbump.png" alt="The Emergency Calm Cards" loading="lazy" className="w-full h-auto object-cover" style={{
                    aspectRatio: '16/10'
                  }} data-id="element-586" />
                  </div>
                </div>

                {/* Content area */}
                <div className="px-5 pb-5" data-id="element-587">
                  {/* Checkbox + title row */}
                  <div className="flex items-start gap-3 mb-3" data-id="element-588">
                    <div className={`w-5 h-5 rounded-md border-2 shrink-0 mt-0.5 flex items-center justify-center transition-all duration-200 ${bump1 ? 'bg-[#8B9E81] border-[#8B9E81]' : 'border-[#C8BFB0] bg-white'}`} data-id="element-589">
                      {bump1 && <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-590" />}
                    </div>
                    <p className="font-serif text-sm md:text-base font-bold text-[#3D3229] leading-snug flex-1" data-id="element-591">
                      YES — Add The Emergency Calm Cards for{' '}
                      <span className="text-[#999] line-through text-xs font-normal mr-1 font-serif italic" data-id="element-592">
                        {formatPrice(14)}
                      </span>
                      <span className="font-serif" data-id="element-593">{formatPrice(7)}</span>
                    </p>
                    <span className="inline-block bg-[#8B9E81] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full whitespace-nowrap shrink-0 shadow-sm font-serif" data-id="element-594">
                      +{formatPrice(7)}
                    </span>
                  </div>

                  {/* Thin divider */}
                  <div className="h-px bg-[#E5DDD3]/60 mb-3 ml-8" data-id="element-595" />

                  {/* Description */}
                  <div data-id="element-596">
                    <p className="text-xs text-[#696969] leading-relaxed mb-3" data-id="element-597">
                      When naps go sideways, you don't need more advice — you
                      need an anchor. 10 minimalist Calm Cards you can save to
                      your phone for instant in-the-moment resets.
                    </p>
                    <div className="space-y-2 mb-3" data-id="element-598">
                      {['Stop the spiral fast', 'Designed for exhaustion', 'Instant access — use today'].map((point, j) => <div key={j} className="flex items-center gap-2.5" data-id="element-599">
                          <Check className="w-3 h-3 text-[#8B9E81] shrink-0" strokeWidth={2.5} data-id="element-600" />
                          <span className="text-[11px] text-[#525252] font-medium" data-id="element-601">
                            {point}
                          </span>
                        </div>)}
                    </div>
                    <p className="text-[11px] text-[#8B9E81] italic font-serif" data-id="element-602">
                      Your calm-down toolkit — always in your pocket.
                    </p>
                  </div>
                </div>
              </div>

              {/* Bump 2 — Sanctuary Shields */}
              <div onClick={() => setBump2(!bump2)} className="relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 order-bump-shimmer" style={{
              background: bump2 ? 'linear-gradient(160deg, rgba(139,158,129,0.06) 0%, #FAF7F2 100%)' : '#FAF7F2',
              border: bump2 ? '1.5px solid #8B9E81' : '1px solid #E5DDD3',
              boxShadow: bump2 ? '0 4px 24px rgba(139,158,129,0.15), 0 0 0 1px rgba(139,158,129,0.1)' : '0 2px 12px rgba(61,50,41,0.04)'
            }} data-id="element-603">
                {/* Recommended badge */}
                <div className="absolute top-3 right-3 z-10" data-id="element-604">
                  <span className="bg-[#8B9E81] text-white text-[9px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md" data-id="element-605">
                    Recommended
                  </span>
                </div>

                {/* Eyebrow label */}
                <div className="px-5 pt-4 pb-0" data-id="element-606">
                  <div className="flex items-center gap-2 mb-3" data-id="element-607">
                    <svg className="w-3.5 h-3.5 text-[#8B9E81]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-608">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" data-id="element-609" />
                    </svg>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-[#8B9E81] font-bold" data-id="element-610">
                      Add-on
                    </span>
                  </div>
                </div>

                {/* Mockup image */}
                <div className="px-5 mb-4" data-id="element-611">
                  <div className="rounded-xl overflow-hidden" style={{
                  boxShadow: '0 6px 20px rgba(61,50,41,0.08)'
                }} data-id="element-612">
                    <img src="https://cdn.magicpatterns.com/uploads/uCcoLQh7Jfr5HPXNzSDmhW/mockup_2orderbump.png" alt="The Sanctuary Shields" loading="lazy" className="w-full h-auto object-cover" style={{
                    aspectRatio: '16/10'
                  }} data-id="element-613" />
                  </div>
                </div>

                {/* Content area */}
                <div className="px-5 pb-5" data-id="element-614">
                  {/* Checkbox + title row */}
                  <div className="flex items-start gap-3 mb-3" data-id="element-615">
                    <div className={`w-5 h-5 rounded-md border-2 shrink-0 mt-0.5 flex items-center justify-center transition-all duration-200 ${bump2 ? 'bg-[#8B9E81] border-[#8B9E81]' : 'border-[#C8BFB0] bg-white'}`} data-id="element-616">
                      {bump2 && <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-617" />}
                    </div>
                    <p className="font-serif text-sm md:text-base font-bold text-[#3D3229] leading-snug flex-1" data-id="element-618">
                      YES — Add The Sanctuary Shields for{' '}
                      <span className="text-[#999] line-through text-xs font-normal mr-1 font-serif italic" data-id="element-619">
                        {formatPrice(8)}
                      </span>
                      <span className="font-serif" data-id="element-620">{formatPrice(4)}</span>
                    </p>
                    <span className="inline-block bg-[#8B9E81] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full whitespace-nowrap shrink-0 shadow-sm font-serif" data-id="element-621">
                      +{formatPrice(4)}
                    </span>
                  </div>

                  {/* Thin divider */}
                  <div className="h-px bg-[#E5DDD3]/60 mb-3 ml-8" data-id="element-622" />

                  {/* Description */}
                  <div data-id="element-623">
                    <p className="text-xs text-[#696969] leading-relaxed mb-3" data-id="element-624">
                      4 elegant printable signs to prevent knocks, doorbells and
                      interruptions mid-transfer. Protect the nap. Protect your
                      peace.
                    </p>
                    <div className="space-y-2 mb-3" data-id="element-625">
                      {['Stops interruptions before they happen', 'Partner-ready — no explanation needed', 'Pretty enough to leave up permanently'].map((point, j) => <div key={j} className="flex items-center gap-2.5" data-id="element-626">
                          <Check className="w-3 h-3 text-[#8B9E81] shrink-0" strokeWidth={2.5} data-id="element-627" />
                          <span className="text-[11px] text-[#525252] font-medium" data-id="element-628">
                            {point}
                          </span>
                        </div>)}
                    </div>
                    <p className="text-[11px] text-[#8B9E81] italic font-serif" data-id="element-629">
                      One less thing to worry about during nap time.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary — always visible */}
            <div className="mt-6 rounded-2xl p-4 md:p-6 lg:p-7 text-left relative overflow-hidden" style={{
            background: 'linear-gradient(160deg, rgba(250,247,242,0.16) 0%, rgba(250,247,242,0.07) 100%)',
            border: '1px solid rgba(250,247,242,0.18)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.10), 0 1px 6px rgba(0,0,0,0.06)'
          }} data-id="element-630">
              {/* Subtle glow */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-sage/10 rounded-full blur-2xl pointer-events-none" data-id="element-631" />

              {/* Header */}
              <div className="flex items-center gap-2.5 mb-5 relative z-10 pb-3 border-b border-cream/10" data-id="element-632">
                <div className="w-7 h-7 rounded-lg bg-cream/10 flex items-center justify-center" data-id="element-633">
                  <svg className="w-3.5 h-3.5 text-cream/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-634">
                    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" data-id="element-635" />
                    <line x1="3" y1="6" x2="21" y2="6" data-id="element-636" />
                    <path d="M16 10a4 4 0 0 1-8 0" data-id="element-637" />
                  </svg>
                </div>
                <p className="text-cream/70 text-xs uppercase tracking-[0.18em] font-bold" data-id="element-638">
                  Your Order
                </p>
              </div>

              <div className="space-y-2 relative z-10" data-id="element-639">
                {/* Base product */}
                <div className="flex justify-between items-start text-cream/90 py-2.5 px-3 md:py-3 md:px-4 rounded-xl bg-cream/[0.07]" data-id="element-640">
                  <div className="flex items-start gap-2 min-w-0" data-id="element-641">
                    <div className="w-1.5 h-1.5 rounded-full bg-terracotta/60 shrink-0 mt-1.5" data-id="element-642" />
                    <span className="font-medium text-[13px] md:text-[15px] leading-tight" data-id="element-643">
                      The Nap-Trapped Reset + 3 Bonuses
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2 md:gap-3 shrink-0 ml-2 md:ml-4" data-id="element-644">
                    <span className="font-serif text-cream/30 text-[11px] md:text-xs italic line-through" data-id="element-645">
                      {formatPrice(45)}
                    </span>
                    <span className="font-serif font-bold text-sm md:text-base text-cream" data-id="element-646">
                      {formatPrice(17)}
                    </span>
                  </div>
                </div>

                {/* Bump 1 */}
                <AnimatePresence data-id="element-647">
                  {bump1 && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.25,
                  ease: [0.25, 0.1, 0.25, 1]
                }} className="flex justify-between items-center text-cream/90 py-2.5 px-3 md:py-3 md:px-4 rounded-xl bg-cream/[0.04]" data-id="element-648">
                      <div className="flex items-center gap-2 min-w-0" data-id="element-649">
                        <div className="w-1.5 h-1.5 rounded-full bg-sage/60 shrink-0" data-id="element-650" />
                        <span className="font-medium text-[13px] md:text-[15px]" data-id="element-651">
                          Emergency Calm Cards
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 md:gap-3 shrink-0 ml-2 md:ml-4" data-id="element-652">
                        <span className="font-serif text-cream/30 text-[11px] md:text-xs italic line-through" data-id="element-653">
                          {formatPrice(14)}
                        </span>
                        <span className="font-serif font-bold text-sm md:text-base text-cream" data-id="element-654">
                          {formatPrice(7)}
                        </span>
                      </div>
                    </motion.div>}
                </AnimatePresence>

                {/* Bump 2 */}
                <AnimatePresence data-id="element-655">
                  {bump2 && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.25,
                  ease: [0.25, 0.1, 0.25, 1]
                }} className="flex justify-between items-center text-cream/90 py-2.5 px-3 md:py-3 md:px-4 rounded-xl bg-cream/[0.07]" data-id="element-656">
                      <div className="flex items-center gap-2 min-w-0" data-id="element-657">
                        <div className="w-1.5 h-1.5 rounded-full bg-sage/60 shrink-0" data-id="element-658" />
                        <span className="font-medium text-[13px] md:text-[15px]" data-id="element-659">
                          Sanctuary Shields
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 md:gap-3 shrink-0 ml-2 md:ml-4" data-id="element-660">
                        <span className="font-serif text-cream/30 text-[11px] md:text-xs italic line-through" data-id="element-661">
                          {formatPrice(8)}
                        </span>
                        <span className="font-serif font-bold text-sm md:text-base text-cream" data-id="element-662">
                          {formatPrice(4)}
                        </span>
                      </div>
                    </motion.div>}
                </AnimatePresence>

                {/* Total row */}
                <div className="flex justify-between items-center mt-3 py-4 px-4 md:py-5 md:px-5 rounded-xl" style={{
                background: 'linear-gradient(135deg, rgba(139,158,129,0.15) 0%, rgba(139,158,129,0.08) 100%)',
                border: '1px solid rgba(139,158,129,0.2)'
              }} data-id="element-663">
                  <span className="text-cream/90 font-bold text-sm md:text-base uppercase tracking-widest" data-id="element-664">
                    Total
                  </span>
                  <div className="flex items-baseline gap-3 md:gap-4" data-id="element-665">
                    <span className="font-serif text-cream/30 text-sm md:text-base italic line-through" data-id="element-666">
                      {formatPrice(45 + (bump1 ? 14 : 0) + (bump2 ? 8 : 0))}
                    </span>
                    <span className="font-serif text-cream font-bold text-xl md:text-3xl tracking-tight" data-id="element-667">
                      {formatPrice(totalPrice)}
                    </span>
                  </div>
                </div>

                {/* Savings badge */}
                <div className="flex justify-center pt-4" data-id="element-668">
                  <motion.span key={totalPrice} initial={{
                  scale: 0.95,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} transition={{
                  duration: 0.3,
                  ease: [0.25, 0.1, 0.25, 1]
                }} className="inline-flex items-center gap-2 bg-[#8B9E81]/10 border border-[#8B9E81]/20 text-[#8B9E81] text-[10px] uppercase tracking-widest font-medium px-4 py-1.5 rounded-full" data-id="element-669">
                    <Sparkles className="w-3 h-3" strokeWidth={2} data-id="element-670" />
                    Savings:{' '}
                    <span className="font-serif text-xs tracking-normal line-through opacity-60" data-id="element-671">
                      {formatPrice(45 + (bump1 ? 14 : 0) + (bump2 ? 8 : 0) - totalPrice)}
                    </span>
                    <span className="text-[10px] opacity-70" data-id="element-672">off</span>
                  </motion.span>
                </div>
              </div>
            </div>

            {/* Checkout button below order summary */}
            <motion.button onClick={goToCheckout} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="mt-5 w-full py-4 md:py-5 rounded-2xl font-bold text-base md:text-lg tracking-wide text-cream flex items-center justify-center gap-2.5 transition-all duration-300 cursor-pointer" style={{
            background: 'linear-gradient(135deg, #7B8E6B 0%, #657556 100%)',
            boxShadow: '0 4px 20px rgba(123,142,107,0.3), 0 1px 4px rgba(0,0,0,0.1)'
          }} data-id="element-673">
              Complete My Order
              <svg className="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-674">
                <path d="M5 12h14" data-id="element-675" />
                <path d="m12 5 7 7-7 7" data-id="element-676" />
              </svg>
            </motion.button>
          </div>
        </motion.div>

        {/* Guarantee block */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="rounded-2xl p-6 text-center" style={{
        background: 'rgba(123,142,107,0.08)',
        border: '1px solid rgba(123,142,107,0.15)'
      }} data-id="element-677">
          <div className="flex items-center justify-center gap-2 mb-3" data-id="element-678">
            <ShieldCheck className="w-4 h-4 text-sage" data-id="element-679" />
            <p className="text-sm font-bold text-cream/80 uppercase tracking-widest" data-id="element-680">
              14-Day Gentle Guarantee
            </p>
          </div>
          <p className="text-cream/50 text-sm leading-relaxed" data-id="element-681">
            Try it. If you don't feel more supported and regulated, just reach
            out.
            <br data-id="element-682" />
            <span className="text-cream/70" data-id="element-683">No stress. No shame.</span>
          </p>
        </motion.div>
      </motion.div>
    </section>;
}